import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { fetchListings } from '../api/services';
import { useDebounce } from '../hooks';
import {
  EmptyState,
  ErrorDisplay,
  PermissionChip,
  PaginationControl,
  SectionHeader,
  ListingCard,
  ListingCardSkeleton,
} from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';
import type { Permission, ListingFilters } from '../types';
import { useFetch } from '../hooks';
import { useI18n } from '../i18n';

// ============================================================
// FILTER SIDEBAR
// ============================================================

const PERMISSION_OPTIONS: Permission[] = ['BF2', 'BF3', 'BF4', 'HIPO'];

interface FilterSidebarProps {
  filters: ListingFilters;
  onFiltersChange: (filters: Partial<ListingFilters>) => void;
  onClose?: () => void;
}

function FilterSidebar({ filters, onFiltersChange, onClose }: FilterSidebarProps) {
  const { t } = useI18n();

  const handlePermissionToggle = (permission: Permission) => {
    const currentPermissions = filters.permissions ?? [];
    const updatedPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter((p) => p !== permission)
      : [...currentPermissions, permission];
    onFiltersChange({ permissions: updatedPermissions });
  };

  return (
    <Box sx={{ p: 2 }}>
      {onClose && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('filters.title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Permissions filter */}
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
          {t('listings.requiredPermissions')}
        </FormLabel>
        <FormGroup>
          {PERMISSION_OPTIONS.map((permission) => (
            <FormControlLabel
              key={permission}
              control={
                <Checkbox
                  checked={filters.permissions?.includes(permission) ?? false}
                  onChange={() => handlePermissionToggle(permission)}
                  size="small"
                  sx={{ color: DESIGN_TOKENS.colors.primary }}
                />
              }
              label={
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PermissionChip permission={permission} />
                </Typography>
              }
            />
          ))}
        </FormGroup>
      </FormControl>

      {/* Sort */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
          {t('listings.sort')}
        </FormLabel>
        <Select
          size="small"
          value={`${filters.sortBy ?? 'createdAt'}_${filters.sortOrder ?? 'desc'}`}
          onChange={(event) => {
            const [sortBy, sortOrder] = event.target.value.split('_');
            onFiltersChange({ sortBy, sortOrder: sortOrder as 'asc' | 'desc' });
          }}
        >
          <MenuItem value="createdAt_desc">{t('listings.newest')}</MenuItem>
          <MenuItem value="createdAt_asc">{t('listings.oldest')}</MenuItem>
          <MenuItem value="order_value_desc">{t('listings.sortValueDesc')}</MenuItem>
          <MenuItem value="order_value_asc">{t('listings.sortValueAsc')}</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        fullWidth
        onClick={() => onFiltersChange({ permissions: [], sortBy: 'createdAt', sortOrder: 'desc' })}
        size="small"
      >
        {t('listings.clearFilters')}
      </Button>
    </Box>
  );
}

// ============================================================
// LISTINGS PAGE
// ============================================================

export default function ListingsPage() {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = Number(searchParams.get('page') ?? '1');
    return Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  });
  const [activeFilters, setActiveFilters] = useState<ListingFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
    pageSize: 9,
  });
  const [isMobileFilterDrawerOpen, setIsMobileFilterDrawerOpen] = useState(false);

  const debouncedSearchQuery = useDebounce(searchInputValue, 400);

  const appliedFilters: ListingFilters = {
    ...activeFilters,
    searchQuery: debouncedSearchQuery || undefined,
    page: currentPage,
  };

  const { data: listingsData, isLoading, error, refetch } = useFetch(
    () => fetchListings(appliedFilters),
    [debouncedSearchQuery, currentPage, activeFilters]
  );

  const handleFiltersChange = useCallback((newFilters: Partial<ListingFilters>) => {
    setActiveFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setCurrentPage(1);
    }
  };

  const totalPages = listingsData?.meta?.pagination?.pageCount ?? 1;
  const totalItems = listingsData?.meta?.pagination?.total ?? 0;

  React.useEffect(() => {
    const pageParam = Number(searchParams.get('page') ?? '1');
    const nextPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }
  }, [currentPage, searchParams]);

  React.useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);

    if (currentPage > 1) {
      nextParams.set('page', String(currentPage));
    } else {
      nextParams.delete('page');
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [currentPage, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(totalPages, 1));
    }
  }, [currentPage, totalPages]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          <SectionHeader
            title={t('listings.pageTitle')}
            subtitle={t('listings.pageSubtitle')}
          />
          {/* Search bar */}
          <Box sx={{ maxWidth: 640 }}>
            <TextField
              fullWidth
              placeholder={t('listings.searchPlaceholderExtended')}
              value={searchInputValue}
              onChange={(event) => setSearchInputValue(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: DESIGN_TOKENS.colors.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: DESIGN_TOKENS.colors.surface }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Desktop Sidebar */}
          {!isMobileView && (
            <Grid item md={3} lg={2.5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 80,
                  backgroundColor: DESIGN_TOKENS.colors.surface,
                  borderRadius: DESIGN_TOKENS.borderRadius.card,
                  border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`, backgroundColor: DESIGN_TOKENS.colors.primaryLighter }}>
                  <Typography variant="h6">{t('filters.title')}</Typography>
                </Box>
                <FilterSidebar filters={activeFilters} onFiltersChange={handleFiltersChange} />
              </Box>
            </Grid>
          )}

          {/* Main Content */}
          <Grid item xs={12} md={9} lg={9.5}>
            {/* Results header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? t('common.loading') : t('listings.resultsSummary', { total: totalItems })}
              </Typography>
              {isMobileView && (
                <Button
                  startIcon={<FilterListIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setIsMobileFilterDrawerOpen(true)}
                >
                  {t('filters.title')}
                </Button>
              )}
            </Box>

            {error && <ErrorDisplay message={error} onRetry={refetch} />}

            {!error && (
              <Grid container spacing={2.5}>
                {isLoading
                  ? Array.from({ length: 9 }).map((_, skeletonIndex) => (
                      <Grid item xs={12} sm={6} lg={4} key={`skeleton-${skeletonIndex}`}>
                        <ListingCardSkeleton />
                      </Grid>
                    ))
                  : listingsData?.data?.map((listing) => (
                      <Grid item xs={12} sm={6} lg={4} key={listing.id}>
                        <ListingCard listing={listing} />
                      </Grid>
                    ))}
              </Grid>
            )}

            {!isLoading && !error && listingsData?.data?.length === 0 && (
              <EmptyState
                title={t('listings.noResultsTitle')}
                description={t('listings.noResultsLong')}
                actionLabel={t('listings.clearFilters')}
                onAction={() => {
                  setSearchInputValue('');
                  setActiveFilters({ sortBy: 'createdAt', sortOrder: 'desc', pageSize: 9 });
                  setCurrentPage(1);
                }}
              />
            )}

            <PaginationControl
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={activeFilters.pageSize}
              onPageChange={setCurrentPage}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={isMobileFilterDrawerOpen}
        onClose={() => setIsMobileFilterDrawerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' } }}
      >
        <FilterSidebar
          filters={activeFilters}
          onFiltersChange={handleFiltersChange}
          onClose={() => setIsMobileFilterDrawerOpen(false)}
        />
      </Drawer>
    </Box>
  );
}
